/**
 * Module: screens/creator/CreatorOnboardingScreen
 * Role: Wizard root — routes between 7 onboarding steps (FTR-1106 amendment
 *       2026-06-07, design Eqq6AbBPpm1zPPyErlkgJQ chat16): url → selfie →
 *       consent → categories → formats → demographics → cities → (submit) →
 *       /creator-status. Screenshot step removed (auto-checked by API).
 *       Handles legacy short-cut: if creator_status=verified and
 *       creator_profile_complete=false, redirect to /creator-onboarding/categories
 *       (first profile step after the new flattening).
 *
 * Uses:
 *   - react-router-dom (Routes, Route, Navigate, useNavigate)
 *   - api/creator:getCreatorStatus
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/steps/*
 *
 * Used by: App.tsx route /creator-onboarding/*
 * Glossary: ai/glossary/buyer.md
 */

import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { getCreatorStatus } from '../../api/creator';
import { P, T } from '../../styles/tokens';
import PromoScreen from './steps/PromoScreen';
import UrlStep from './steps/UrlStep';
import SelfieStep from './steps/SelfieStep';
import ConsentStep from './steps/ConsentStep';
import CategoriesStep from './steps/CategoriesStep';
import FormatsStep from './steps/FormatsStep';
import DemographicsStep from './steps/DemographicsStep';
import CitiesStep from './steps/CitiesStep';

type InitState = 'loading' | 'legacy' | 'normal' | 'error';

export default function CreatorOnboardingScreen() {
  const [initState, setInitState] = useState<InitState>('loading');

  // Legacy short-cut: verified buyers without profile skip first 4 steps
  useEffect(() => {
    let cancelled = false;
    getCreatorStatus()
      .then((state) => {
        if (cancelled) return;
        // L-4: legacy verified creator WITHOUT profile → skip to the first
        // profile step (CategoriesStep). Spec §D: redirect ONLY when
        // creator_status='verified' AND creator_profile_complete=false.
        // Fully-onboarded creators (creator_profile_complete=true) must NOT
        // be redirected. Pre-2026-06-07 this jumped to a single ProfileStep
        // which has been split into 4 separate steps; CategoriesStep is the
        // new entry point and walks the user through formats/demographics/
        // cities before submit.
        const isLegacy =
          state.account_kind === 'creator' &&
          state.application?.status === 'approved' &&
          state.creator_profile_complete === false;
        if (isLegacy) {
          setInitState('legacy');
        } else {
          setInitState('normal');
        }
      })
      .catch(() => {
        if (!cancelled) setInitState('normal');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (initState === 'loading') {
    return (
      <div style={{ padding: 24, textAlign: 'center', font: T.subhead, color: P.inkSoft }}>
        Загрузка…
      </div>
    );
  }

  // Legacy short-cut redirect — first profile step after FTR-1106 amendment.
  if (initState === 'legacy') {
    return <Navigate to="/creator-onboarding/categories" replace />;
  }

  return (
    <Routes>
      {/* FTR-1131 amendment 2026-06-07: promo is the wizard entry point.
          Home/Tasks/Profile promo cards now route here (not directly to /url),
          fixing «Кнопка "Стать креатором" ведёт сразу на верификацию» report. */}
      <Route index element={<Navigate to="promo" replace />} />
      <Route path="promo" element={<PromoScreen />} />
      <Route path="url" element={<UrlStep />} />
      <Route path="selfie" element={<SelfieStep />} />
      <Route path="consent" element={<ConsentStep />} />
      <Route path="categories" element={<CategoriesStep />} />
      <Route path="formats" element={<FormatsStep />} />
      <Route path="demographics" element={<DemographicsStep />} />
      <Route path="cities" element={<CitiesStep />} />
      {/* Pre-2026-06-07 routes: redirect into the new flow so existing
          deep-links (`screenshot`, `profile`) don't 404. */}
      <Route path="screenshot" element={<Navigate to="../consent" replace />} />
      <Route path="profile" element={<Navigate to="../categories" replace />} />
      <Route path="*" element={<Navigate to="promo" replace />} />
    </Routes>
  );
}
